import { sensorsUrl } from "../api/endpoints";
import axios from 'axios';
import React, { useState, useEffect, useMemo } from 'react';

import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import './SegmentSwitch.css';

const getPositiveNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const SegmentSwitch = ({
  activeOptionColor,
  activeBackgroundColor,
  activeTextColor,
  inactiveTextColor,
  inactiveBackgroundColor,
  fontSize,
  labelFontSize,
  valueFontSize,
  paddingLeft,
  paddingRight,
  paddingTop,
  paddingBottom,
  title,
  transparentBackground,
  options,
  value,
  draggable,
  pin,
  did,
  tid,
  visualStyle = 'glass',
}) => {
  const [selectedValue, setSelectedValue] = useState(value);
  const [cursorState, setCursorState] = useState('idle');

  useEffect(() => {
    const selectedItem = options?.find((x) => String(x.value) === String(value));
    setSelectedValue(selectedItem?.value ?? value);
  }, [value, options]);

  const resolvedValueFontSize = getPositiveNumber(valueFontSize || fontSize);
  const valueLabelStyle = resolvedValueFontSize
    ? { fontSize: `calc(${resolvedValueFontSize}px * var(--font-size-multiplier, 1))` }
    : undefined;

  const rootStyle = useMemo(() => {
    const style = {};
    const resolvedActiveBackground = activeBackgroundColor || activeOptionColor;
    const resolvedTitleFontSize = getPositiveNumber(labelFontSize);
    const resolvedFontSize = getPositiveNumber(valueFontSize || fontSize);
    const resolvePadding = (value) => {
      const parsed = Number(value);
      return Number.isFinite(parsed) && parsed >= 0 ? parsed : 8;
    };

    if (resolvedActiveBackground) {
      style['--segment-accent'] = resolvedActiveBackground;
      style['--segment-bg-selected'] = resolvedActiveBackground;
      style['--segment-selected-bg-mixed'] = resolvedActiveBackground;
      style['--segment-bg-selected-hover'] = resolvedActiveBackground;
      style['--segment-selected-hover-bg-mixed'] = resolvedActiveBackground;
    }
    if (inactiveBackgroundColor) {
      style['--segment-group-bg'] = inactiveBackgroundColor;
    }
    if (activeTextColor) {
      style['--segment-selected-text'] = activeTextColor;
    }
    if (inactiveTextColor) {
      style['--segment-unselected-text'] = inactiveTextColor;
    }
    if (resolvedFontSize) {
      style['--segment-font-size'] = `${resolvedFontSize}px`;
      style['--segment-button-min-height'] = `${Math.max(39, resolvedFontSize * 2.4)}px`;
      style['--segment-button-radius'] = `${Math.max(8, resolvedFontSize * 0.5)}px`;
    }
    if (resolvedTitleFontSize) {
      style['--segment-title-size-final'] = `${resolvedTitleFontSize}px`;
    }
    style['--segment-padding-left'] = `${resolvePadding(paddingLeft)}px`;
    style['--segment-padding-right'] = `${resolvePadding(paddingRight)}px`;
    style['--segment-padding-top'] = `${resolvePadding(paddingTop)}px`;
    style['--segment-padding-bottom'] = `${resolvePadding(paddingBottom)}px`;

    return Object.keys(style).length > 0 ? style : undefined;
  }, [
    activeBackgroundColor,
    activeOptionColor,
    inactiveBackgroundColor,
    activeTextColor,
    inactiveTextColor,
    fontSize,
    labelFontSize,
    paddingBottom,
    paddingLeft,
    paddingRight,
    paddingTop,
    valueFontSize,
  ]);

  const handleChange = (event, newValue) => {
    if (newValue !== null) {
      setSelectedValue(newValue);

      if (did) {
        const body = JSON.stringify({
          device: did,
          template: tid,
          data: { [pin]: newValue.toString() },
        });

        axios
          .post(sensorsUrl(`control`), body, {
            headers: { 'Content-Type': 'application/json' },
          })
          .then((res) => console.log(res.data))
          .catch(() => setSelectedValue(value));
      }
    }
  };

  const cursorClass = draggable
    ? cursorState === 'grabbing'
      ? 'segment-switch--grabbing'
      : 'segment-switch--grab'
    : 'segment-switch--auto';

  return (
    <div
      className={[
        'widget-container',
        'segment-switch',
        visualStyle === 'classic' ? 'segment-switch--classic' : 'segment-switch--glass',
        cursorClass,
        transparentBackground ? 'segment-switch--transparent' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      style={rootStyle}
      onMouseDown={() => draggable && setCursorState('grabbing')}
      onMouseUp={() => draggable && setCursorState('grab')}
      onMouseLeave={() => draggable && setCursorState('grab')}
      draggable={draggable}
      onDragStart={(e) => {
        const id = e.target.closest('.widget')?.id;
        if (!id) return;

        const rect = e.target.getBoundingClientRect();
        const offsetX = e.clientX - rect.x;
        const offsetY = e.clientY - rect.y;

        const dragData = `segment-switch;${id};${offsetX};${offsetY};${id.startsWith('base') ? 'add' : 'move'}`;
        e.dataTransfer.setData('text/plain', dragData);
      }}
    >
      {title ? <h3 className="segment-switch__title">{title}</h3> : null}

      <div className="segment-switch__wrap">
        <ToggleButtonGroup
          value={selectedValue}
          exclusive
          onChange={handleChange}
          color="primary"
          className="segment-switch__group"
        >
          {options?.map((option) => (
            <ToggleButton
              key={option.value}
              value={option.value}
              size="small"
              aria-pressed={String(selectedValue) === String(option.value)}
              className={[
                'segment-switch__btn',
                String(selectedValue) === String(option.value) ? 'segment-switch__btn--selected' : '',
              ].filter(Boolean).join(' ')}
            >
              <span className="segment-switch__btnLabel" style={valueLabelStyle}>{option.label}</span>
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </div>
    </div>
  );
};

export default SegmentSwitch;
